import type {
  CaseInformationResponse,
  CreateCaseRequest,
  CreateCaseResponse,
  GetCaseByIdRequest,
  GetCaseStatusesResponse,
  UpdateCaseRequest,
  UpdateCaseResponse,
  GetCaseCategoriesResponse,
  GetCaseTypesResponse,
  GetCaseTypesByCategoryResponse,
  GetCasePrioritiesResponse,
  GetSubCaseTypeTreeResponse,
  GetCaseResolutionRequest,
  GetCaseResolutionResponse,
  GetCaseOriginationsResponse,
  GetEmployeesByWorkspaceResponse,
  GetInternalUsersResponse,
  GetResolutionTypesResponse,
  GetClientContactsResponse,
  LinkRelatedCaseRequest,
  LinkRelatedCaseResponse,
  GetCaseTimersResponse,
  GetTimeTrackingCategoriesResponse,
  UpdateCaseTimerRequest,
} from '@/types/case';
import { API_ENDPOINTS } from '@/constants';
import type { ApiBuilder } from './types';

export const caseApi = (builder: ApiBuilder) => ({
  createCase: builder.mutation<CreateCaseResponse, CreateCaseRequest>({
    query: payload => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}${API_ENDPOINTS.CASE.CREATE_CASE}`,
      method: 'POST',
      body: payload,
    }),
    invalidatesTags: ['Case'],
  }),
  getCaseById: builder.query<CaseInformationResponse, GetCaseByIdRequest>({
    query: ({ caseId }) => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}/${encodeURIComponent(String(caseId))}`,
      method: 'GET',
      cache: 'no-store',
    }),
    providesTags: ['Case'],
  }),
  updateCase: builder.mutation<UpdateCaseResponse, UpdateCaseRequest>({
    query: ({ caseId, ...payload }) => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}/${encodeURIComponent(String(caseId))}`,
      method: 'PUT',
      body: payload,
    }),
    invalidatesTags: ['Case'],
  }),
  linkRelatedCase: builder.mutation<
    LinkRelatedCaseResponse,
    LinkRelatedCaseRequest
  >({
    query: ({ caseId, ...payload }) => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}/${encodeURIComponent(String(caseId))}${API_ENDPOINTS.CASE.RELATED_CASE}`,
      method: 'POST',
      body: payload,
    }),
    invalidatesTags: ['Case'],
  }),
  getCaseStatuses: builder.query<GetCaseStatusesResponse, void>({
    query: () => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}${API_ENDPOINTS.CASE.CASE_STATUS}`,
      method: 'GET',
    }),
    providesTags: ['Case'],
  }),
  getCaseCategories: builder.query<GetCaseCategoriesResponse, void>({
    query: () => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}${API_ENDPOINTS.CASE.CASE_CATEGORIES}`,
      method: 'GET',
    }),
    providesTags: ['Case'],
  }),
  getCaseTypes: builder.query<GetCaseTypesResponse, void>({
    query: () => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}${API_ENDPOINTS.CASE.CASE_TYPES}`,
      method: 'GET',
    }),
    providesTags: ['Case'],
  }),
  getCasePriorities: builder.query<GetCasePrioritiesResponse, void>({
    query: () => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}${API_ENDPOINTS.CASE.CASE_PRIORITIES}`,
      method: 'GET',
    }),
    providesTags: ['Case'],
  }),
  getSubCaseTypeTree: builder.query<GetSubCaseTypeTreeResponse, void>({
    query: () => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}${API_ENDPOINTS.CASE.CASE_SUB_CASE_TYPE_TREE}`,
      method: 'GET',
    }),
    providesTags: ['Case'],
  }),
  getCaseResolution: builder.query<
    GetCaseResolutionResponse,
    GetCaseResolutionRequest
  >({
    query: ({ contactId }) => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}${API_ENDPOINTS.CASE.CASE_RESOLUTION}/${encodeURIComponent(contactId)}`,
      method: 'GET',
    }),
  }),
  getCaseOriginations: builder.query<GetCaseOriginationsResponse, void>({
    query: () => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}${API_ENDPOINTS.CASE.CASE_ORIGINATIONS}`,
      method: 'GET',
    }),
    providesTags: ['Case'],
  }),
  getCaseTypesByCategory: builder.query<GetCaseTypesByCategoryResponse, string>(
    {
      query: categoryCode => ({
        url: `${API_ENDPOINTS.CASE.BASE_URL}${API_ENDPOINTS.CASE.CASE_TYPES_BY_CATEGORY}/${encodeURIComponent(categoryCode)}`,
        method: 'GET',
      }),
      providesTags: ['Case'],
    }
  ),
  getEmployeesByWorkspace: builder.query<
    GetEmployeesByWorkspaceResponse,
    number
  >({
    query: workspaceId => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}${API_ENDPOINTS.CASE.CASE_EMPLOYEES}`,
      method: 'GET',
      params: { workspaceId },
    }),
    providesTags: ['Case'],
  }),
  getClientContacts: builder.query<GetClientContactsResponse, number>({
    query: workspaceId => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}${API_ENDPOINTS.CASE.CASE_CONTACTS}`,
      method: 'GET',
      params: { workspaceId },
    }),
    providesTags: ['Case'],
  }),
  getInternalUsers: builder.query<GetInternalUsersResponse, void>({
    query: () => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}${API_ENDPOINTS.CASE.CASE_USERS}`,
      method: 'GET',
    }),
    providesTags: ['Case'],
  }),
  getTimeTrackingCategories: builder.query<
    GetTimeTrackingCategoriesResponse,
    void
  >({
    query: () => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}${API_ENDPOINTS.CASE.TIME_TRACKING_CATEGORIES}`,
      method: 'GET',
    }),
  }),
  getCaseTimers: builder.query<GetCaseTimersResponse, number>({
    query: caseId => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}/${encodeURIComponent(String(caseId))}${API_ENDPOINTS.CASE.TIMERS}`,
      method: 'GET',
    }),
  }),
  updateCaseTimer: builder.mutation<void, UpdateCaseTimerRequest>({
    query: ({ caseId, timerId, ...changes }) => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}/${encodeURIComponent(String(caseId))}${API_ENDPOINTS.CASE.TIMERS}/${encodeURIComponent(String(timerId))}`,
      method: 'PATCH',
      body: changes,
    }),
  }),
  getResolutionTypes: builder.query<GetResolutionTypesResponse, void>({
    query: () => ({
      url: `${API_ENDPOINTS.CASE.BASE_URL}${API_ENDPOINTS.CASE.CASE_RESOLUTION_TYPES}`,
      method: 'GET',
    }),
    providesTags: ['Case'],
  }),
});
